import React, { MouseEventHandler } from 'react';
import cn from 'classnames';
import styles from './Icons.module.scss';
import Occasion from '@repo/utils/helpers/Occasion';
import { NoteDateDisplay } from '@repo/components/notes';
import { useTheme } from '@repo/themes';
import { ReminderAlarmClockIcon } from '.';
import { SVGProps } from '@repo/utils/types';

// const themeBadgeBGColorMap = {
//   upcoming: { light: 'rgba(38,134,190,0.18)', dark: 'rgba(38,134,190,0.18)' },
//   past: { light: 'rgba(72,73,74,0.12)', dark: 'rgba(255,255,255,0.12)' },
// };

export function ReminderBadgeIcon(props: SVGProps & { date: Occasion | null }) {
  const isUpcoming = props.date ? !props.date?.isPast : true;

  return (
    <div
      className={cn(props.className)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        borderRadius: 4,
        // backgroundColor: themeBadgeBGColorMap[timeKey][themeFamily],
        justifySelf: 'center',
      }}
    >
      <ReminderAlarmClockIcon width={14} height={14} color="textSecondary" colorized={isUpcoming} />
    </div>
  );
}

const themeBoltOutlineMap = {
  upcoming: {
    light: {
      opacity: 1,
      stroke: 'url(#reminderBoltStrokeGrad)',
      fillOpacity: 0.16,
      fill: 'url(#reminderBoltFillGrad)',
    },
    dark: {
      opacity: 1,
      stroke: 'url(#reminderBoltStrokeGrad)',
      fillOpacity: 0.16,
      fill: 'url(#reminderBoltFillGrad)',
    },
  },
  past: {
    light: { opacity: 1, stroke: 'rgb(202,202,202)', fillOpacity: 0, fill: 'none' },
    dark: { opacity: 1, stroke: 'rgb(101,101,101)', fillOpacity: 0, fill: 'none' },
  },
  none: {
    light: { opacity: 0.8, stroke: '#48494A', fillOpacity: 0, fill: 'none' },
    dark: { opacity: 0.8, stroke: '#FFFFFF', fillOpacity: 0, fill: 'none' },
  },
};

function ReminderIcon({
  hasDate = true,
  alwaysRender = false,
  isUpcoming = false,
  large = false,
  ...props
}: SVGProps & {
  hasDate?: boolean;
  alwaysRender?: boolean;
  isUpcoming?: boolean;
  large?: boolean;
}) {
  const [_, themeFamily] = useTheme();
  let timeKey = isUpcoming ? 'upcoming' : 'past';
  let pathAttrs = themeBoltOutlineMap[timeKey][themeFamily];

  if (!hasDate && !alwaysRender) {
    return null;
  } else if (!hasDate && alwaysRender) {
    pathAttrs = themeBoltOutlineMap.none[themeFamily];
  }

  if (!large) {
    pathAttrs.fillOpacity = 0;
  }

  return (
    <svg width={props.width} height={props.height} viewBox="0 0 9 13">
      <defs>
        {isUpcoming && (
          <>
            <linearGradient x1="55.420157%" y1="0" x2="42%" y2="99%" id="reminderBoltFillGrad">
              <stop stopColor="#4A90E2" offset="0%"></stop>
              <stop stopColor="#35B5FF" offset="100%"></stop>
            </linearGradient>
            <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="reminderBoltStrokeGrad">
              <stop stopColor="#4A90E2" offset="0%"></stop>
              <stop stopColor="#35B5FF" offset="100%"></stop>
            </linearGradient>
          </>
        )}
      </defs>
      <path
        className={styles.reminderBoltFill}
        fill="none"
        stroke={pathAttrs.stroke}
        strokeWidth="2.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5.4 2 l-2.8 4.4 h 3.8l -2.8 4.4"
      />
      <path
        transform="translate(-3.4, -1.2)"
        strokeLinejoin="round"
        strokeWidth="1.2"
        d="M8.56,2.27 C8.86,2.1 9.09,2.15 9.26,2.23 C9.43,2.32 9.63,2.48 9.66,2.89 L9.66,2.89 L9.09,6.69 L10.71,6.69 C10.9,6.69 11.06,6.73 11.18,6.78 C11.4,6.88 11.54,7.03 11.61,7.2 C11.69,7.37 11.72,7.57 11.65,7.8 C11.61,7.93 11.53,8.07 11.41,8.21 L11.41,8.21 L7.45,12.79 C7.33,12.93 7.22,13.01 7.14,13.06 C6.83,13.25 6.61,13.19 6.44,13.11 C6.28,13.03 6.1,12.89 6.05,12.54 C6.04,12.44 6.04,12.3 6.07,12.13 L6.07,12.13 L6.74,8.57 L5.13,8.57 C4.94,8.57 4.78,8.53 4.66,8.48 C4.44,8.39 4.3,8.24 4.23,8.07 C4.15,7.9 4.12,7.69 4.19,7.46 C4.23,7.34 4.3,7.19 4.43,7.05 L4.43,7.05 L8.25,2.55 C8.36,2.42 8.47,2.33 8.56,2.27 Z"
        {...pathAttrs}
      />
    </svg>
  );
}

// Can this be removed? Need to check if the NotesList can be refactored so as not to use any of the old Reminder UI
export function ReminderDetailIcon({
  alwaysRender = false,
  date = null,
  entityInteracted,
  ...props
}: SVGProps & {
  date: Occasion | null;
  alwaysRender: boolean;
  large: boolean;
  entityInteracted: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
}) {
  let width = props.large ? 16 : 9;
  let height = props.large ? 19 : 13;

  if (!date && !alwaysRender) {
    return null;
  } else if (!date && alwaysRender) {
    // return the base icon with no accompanying text
    return (
      <span className={styles.reminderIconWrapper}>
        <ReminderIcon hasDate={false} alwaysRender width={width} height={height} />
      </span>
    );
  }

  const isUpcoming = !date.isPast;
  const timeKey = isUpcoming ? 'upcoming' : 'past';

  return (
    <div
      className={cn(
        styles.reminderIconWrapper,
        'information',
        styles[timeKey],
        props.large && styles.large,
        entityInteracted && styles.entityInteracted,
        !!props.onClick && styles.clickable,
      )}
      onClick={props.onClick || (() => {})}
    >
      <ReminderIcon
        className={cn()}
        isUpcoming={isUpcoming}
        width={width}
        height={height}
        large={props.large}
      />
      <NoteDateDisplay date={date} />
    </div>
  );
}
